import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAuthHeader } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  QrCode,
  Copy,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COLORS = [
  { value: "blue", label: "Blue", class: "bg-blue-600" },
  { value: "green", label: "Green", class: "bg-green-600" },
  { value: "purple", label: "Purple", class: "bg-purple-600" },
  { value: "orange", label: "Orange", class: "bg-orange-600" },
  { value: "red", label: "Red", class: "bg-red-600" },
  { value: "gray", label: "Gray", class: "bg-gray-600" },
];

export default function TableManagementPage() {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSection, setEditSection] = useState(false);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedQrTable, setSelectedQrTable] = useState(null);

  const [formData, setFormData] = useState({
    table_name: "",
    capacity: 4,
    section_id: "",
  });

  const [sectionFormData, setSectionFormData] = useState({
    name: "",
    color: "blue",
  });

  useEffect(() => {
    fetchTables();
    fetchSections();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeader(),
      });
      setTables(response.data);
    } catch (error) {
      console.error("Error fetching tables:", error);
      toast.error("Failed to load tables");
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections`, {
        headers: getAuthHeader(),
      });
      setSections(response.data);
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editSection) {
        await axios.patch(
          `${API}/sections/${editSection.id}`,
          sectionFormData,
          {
            headers: getAuthHeader(),
          }
        );
        toast.success("Section updated successfully");
      } else {
        await axios.post(`${API}/sections`, sectionFormData, {
          headers: getAuthHeader(),
        });
        toast.success("Section created successfully");
      }
      setEditSection(null);
      setSectionDialogOpen(false);
      setSectionFormData({ name: "", color: "blue" });
      fetchSections();
    } catch (error) {
      console.error("Error saving section:", error);
      toast.error("Failed to save section");
    }
  };

  const deleteSection = async (sectionId) => {
    try {
      // First try normal delete
      await axios.delete(`${API}/sections/${sectionId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Section deleted successfully");
      fetchSections();
      fetchTables();
    } catch (error) {
      console.error("Error deleting section:", error);
      const errorMessage =
        error.response?.data?.detail || "Failed to delete section";

      // Check if error is due to tables using the section
      if (error.response?.status === 400 && errorMessage.includes("table")) {
        // Show confirmation for force delete
        if (
          window.confirm(
            `${errorMessage}\n\nDo you want to force delete this section? Tables will be moved to "Unassigned".`
          )
        ) {
          try {
            // Force delete with query parameter
            await axios.delete(`${API}/sections/${sectionId}?force=true`, {
              headers: getAuthHeader(),
            });
            toast.success(
              "Section deleted and tables reassigned to unassigned"
            );
            fetchSections();
            fetchTables();
          } catch (forceError) {
            console.error("Error force deleting section:", forceError);
            toast.error(
              forceError.response?.data?.detail ||
                "Failed to force delete section"
            );
          }
        }
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const reorderSection = async (sectionId, direction) => {
    try {
      await axios.patch(
        `${API}/sections/${sectionId}/reorder?direction=${direction}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Section reordered");
      fetchSections();
    } catch (error) {
      console.error("Error reordering section:", error);
      toast.error("Failed to reorder section");
    }
  };

  const updateSectionPosition = async (sectionId, newPosition) => {
    try {
      await axios.patch(
        `${API}/sections/${sectionId}/reorder?position=${newPosition}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Section position updated");
      fetchSections();
    } catch (error) {
      console.error("Error updating section position:", error);
      toast.error("Failed to update section position");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingTable) {
        await axios.put(`${API}/tables/${editingTable.id}`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("Table updated successfully");
      } else {
        await axios.post(`${API}/tables`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("Table created successfully");
      }

      setDialogOpen(false);
      setEditingTable(null);
      setFormData({ table_name: "", capacity: 4, section_id: "" });
      fetchTables();
    } catch (error) {
      console.error("Error saving table:", error);
      toast.error("Failed to save table");
    }
  };

  const deleteTable = async (tableId) => {
    if (!window.confirm("Are you sure you want to delete this table?")) return;

    try {
      await axios.delete(`${API}/tables/${tableId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Table deleted successfully");
      fetchTables();
    } catch (error) {
      console.error("Error deleting table:", error);
      toast.error("Failed to delete table");
    }
  };

  const reorderArray = (list, from, to) => {
    const updated = [...list];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated;
  };

  const reorderTable = (tableId, direction) => {
    setTables((prev) => {
      const currentIndex = prev.findIndex((t) => t.id === tableId);
      if (currentIndex === -1) return prev;

      const targetIndex =
        direction === "up" ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex < 0 || targetIndex >= prev.length) return prev;

      toast.success(`Table moved ${direction}`);

      return reorderArray(prev, currentIndex, targetIndex);
    });

    axios
      .patch(
        `${API}/tables/${tableId}/reorder?direction=${direction}`,
        {},
        { headers: getAuthHeader() }
      )
      .catch(() => toast.error("Failed to save table order"));
  };

  const updateTablePosition = (tableId, newPosition) => {
    setTables((prev) => {
      const index = prev.findIndex((t) => t.id === tableId);
      if (index === -1) return prev;

      toast.success("Table position updated");

      return reorderArray(prev, index, newPosition - 1);
    });

    axios
      .patch(
        `${API}/tables/${tableId}/reorder?position=${newPosition}`,
        {},
        { headers: getAuthHeader() }
      )
      .catch(() => toast.error("Failed to save table position"));
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      table_name: table.table_name,
      capacity: table.capacity,
      section_id: table.section_id || "",
    });
    setDialogOpen(true);
  };

  const openEditSection = (section) => {
    setEditSection(section);
    setSectionFormData({
      name: section.name,
      color: section.color,
    });
    setSectionDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingTable(null);
    setFormData({
      table_name: "",
      capacity: 4,
      section_id: sections.length > 0 ? sections[0].id : "",
    });
    setDialogOpen(true);
  };

  const showQR = (table) => {
    setSelectedQrTable(table);
    setQrDialogOpen(true);
  };

  const copyQRUrl = () => {
    const url = `${BACKEND_URL}/order/${selectedQrTable.id}`;

    const copyFallback = (text) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("URL copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy URL");
      }
      document.body.removeChild(textArea);
    };

    // Use clipboard API if available, otherwise fallback to execCommand
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          toast.success("URL copied to clipboard!");
        })
        .catch(() => {
          // Fallback for clipboard API failure
          copyFallback(url);
        });
    } else {
      copyFallback(url);
    }
  };

  const downloadQR = () => {
    try {
      const link = document.createElement("a");
      link.href = selectedQrTable.qr_url; // direct QR image
      link.download = `QR-${selectedQrTable.table_name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("QR Code downloaded!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download QR code");
    }
  };

  const filteredTables =
    selectedSection === "all"
      ? tables
      : tables.filter((t) => t.section_id === selectedSection);

  const getSectionName = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    return section ? section.name : "No Section";
  };

  const getSectionColor = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    return section ? section.color : "gray";
  };

  const tablesBySection = sections.reduce((acc, section) => {
    acc[section.id] = tables.filter((t) => t.section_id === section.id);
    return acc;
  }, {});

  const tablesWithoutSection = tables.filter((t) => !t.section_id);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")}>
                <ArrowLeft size={20} />
              </Button>
              <h1 className="text-2xl font-bold">Table Management</h1>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setSectionDialogOpen(true)}
                variant="outline"
              >
                <Plus size={16} className="mr-2" />
                Create Section
              </Button>
              <Button
                onClick={openNewDialog}
                className="bg-red-600 hover:bg-red-700"
              >
                <Plus size={16} className="mr-2" />
                Add Table
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Section Management */}
        {sections.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Manage Sections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sections.map((section, index) => (
                  <div
                    key={section.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="1"
                        max={sections.length}
                        value={index + 1}
                        onChange={(e) => {
                          const newPos = parseInt(e.target.value);
                          if (newPos > 0 && newPos <= sections.length) {
                            updateSectionPosition(section.id, newPos);
                          }
                        }}
                        className="w-12 text-center text-xs font-bold p-1"
                        title="Position"
                      />
                      <div
                        className={`w-4 h-4 rounded-full ${
                          COLORS.find((c) => c.value === section.color)
                            ?.class || "bg-gray-400"
                        }`}
                      ></div>
                      <span className="font-semibold">{section.name}</span>
                      <span className="text-sm text-gray-500">
                        ({tablesBySection[section.id]?.length || 0})
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reorderSection(section.id, "up")}
                        disabled={index === 0}
                        title="Move Up"
                      >
                        <ArrowUp size={14} className="text-gray-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => reorderSection(section.id, "down")}
                        disabled={index === sections.length - 1}
                        title="Move Down"
                      >
                        <ArrowDown size={14} className="text-gray-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditSection(section)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit size={14} className="text-blue-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSection(section.id)}
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section Filters */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-4">Filter by Section</h2>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedSection === "all" ? "default" : "outline"}
              onClick={() => setSelectedSection("all")}
              size="sm"
            >
              All ({tables.length})
            </Button>
            {sections.map((section) => (
              <Button
                key={section.id}
                variant={selectedSection === section.id ? "default" : "outline"}
                onClick={() => setSelectedSection(section.id)}
                className={
                  selectedSection === section.id
                    ? `${COLORS.find((c) => c.value === section.color)?.class}`
                    : ""
                }
                size="sm"
              >
                {section.name} ({tablesBySection[section.id]?.length || 0})
              </Button>
            ))}
          </div>
        </div>

        {/* Tables Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
          {filteredTables.map((table, index) => (
            <Card
              key={table.id}
              className="hover:shadow-lg transition-all w-full overflow-hidden"
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Input
                      type="number"
                      min="1"
                      max={filteredTables.length}
                      value={index + 1}
                      onChange={(e) => {
                        const newPos = parseInt(e.target.value);
                        if (newPos > 0 && newPos <= filteredTables.length) {
                          updateTablePosition(table.id, newPos);
                        }
                      }}
                      className="w-8 text-center text-xs font-bold p-1"
                      title="Position"
                    />
                    <CardTitle className="text-base sm:text-lg truncate">
                      {table.table_name}
                    </CardTitle>
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => reorderTable(table.id, "up")}
                      disabled={index === 0}
                      className="h-8 w-8 p-0"
                      title="Move Up"
                    >
                      <ArrowUp size={14} className="text-gray-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => reorderTable(table.id, "down")}
                      disabled={index === filteredTables.length - 1}
                      className="h-8 w-8 p-0"
                      title="Move Down"
                    >
                      <ArrowDown size={14} className="text-gray-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => showQR(table)}
                      className="h-8 w-8 p-0"
                    >
                      <QrCode size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(table)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit size={14} className="text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTable(table.id)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Capacity:</span>
                    <span className="font-semibold">
                      {table.capacity} guests
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        table.status === "available"
                          ? "bg-green-100 text-green-700"
                          : table.status === "occupied"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {table.status}
                    </span>
                  </div>
                  {table.section_id && (
                    <div className="pt-2 border-t">
                      <span
                        className={`text-xs font-semibold text-${getSectionColor(
                          table.section_id
                        )}-600`}
                      >
                        📍 {getSectionName(table.section_id)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredTables.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No tables found</p>
            <Button onClick={openNewDialog}>Create Your First Table</Button>
          </div>
        )}
      </div>

      {/* Section Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editSection ? "Edit Section" : "Create New Section"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSectionSubmit} className="space-y-4">
            <div>
              <Label htmlFor="section_name">Section Name</Label>
              <Input
                id="section_name"
                value={sectionFormData.name}
                onChange={(e) =>
                  setSectionFormData({
                    ...sectionFormData,
                    name: e.target.value,
                  })
                }
                placeholder="e.g., Terrace, First Floor"
                required
              />
            </div>
            <div>
              <Label htmlFor="section_color">Color</Label>
              <Select
                value={sectionFormData.color}
                onValueChange={(value) =>
                  setSectionFormData({ ...sectionFormData, color: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-full ${color.class}`}
                        ></div>
                        {color.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                {editSection ? "Update" : "Create"} Section
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSectionDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Table Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTable ? "Edit Table" : "Add New Table"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="table_name">Table Name</Label>
              <Input
                id="table_name"
                value={formData.table_name}
                onChange={(e) =>
                  setFormData({ ...formData, table_name: e.target.value })
                }
                placeholder="e.g., Table 1, A1"
                required
              />
            </div>
            <div>
              <Label htmlFor="capacity">Capacity (Guests)</Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    capacity: parseInt(e.target.value),
                  })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="section">Section</Label>
              <Select
                value={formData.section_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, section_id: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  {sections.map((section) => (
                    <SelectItem key={section.id} value={section.id}>
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                {editingTable ? "Update" : "Create"} Table
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>QR Code for {selectedQrTable?.table_name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            {selectedQrTable?.qr_url ? (
              <>
                <img
                  src={selectedQrTable.qr_url}
                  alt="QR Code"
                  className="w-64 h-64 mb-4"
                />
                <p className="text-sm text-gray-600 text-center mb-4">
                  Customers can scan this QR code to place orders
                </p>
                <div className="w-full space-y-2">
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={copyQRUrl}
                  >
                    <Copy size={16} className="mr-2" />
                    Copy URL
                  </Button>
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={downloadQR}
                  >
                    <QrCode size={16} className="mr-2" />
                    Download QR Code
                  </Button>
                  <div className="p-2 bg-gray-100 rounded text-xs font-mono text-center break-all">
                    {BACKEND_URL}/order/{selectedQrTable.id}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-500">
                No QR code available for this table
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
